import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import arrayMove from 'array-move';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

// Store
import AppState from '@webapp/store/state';
import {
  assetCollaborators,
  collaboratorsList,
  contributorsList,
} from '@webapp/store/collaborator/selectors';
import {
  CreateCollaborator,
  CreateArtistCollaborator,
  RemoveCollaborator,
  UpdateCollaboratorField,
  UpdateCollaboratorOrder,
} from '@webapp/store/collaborator/action-creators';
import { CloseMenu } from '@webapp/store/ui/action-creators';
import { CollaboratorState } from '@webapp/store/collaborator/state';
import { CREATE_COLLABORATOR } from '@webapp/store/collaborator/actions';
import { UpdateReleaseStoreError } from '@webapp/store/release/action-creators';

// Types
import { Option } from '@webapp/types/common/option';
import SimpleArtist from '@common/types/artist/simple-artist';
import { ContactInfo } from '@webapp/types/common/contact-info';
import { ArtistSearchResult } from '@services/artist/types/artist-search-result';

// Services
import { ValidType } from '@services/collaborator/types/valid-types';
import { Collaborator } from '@services/collaborator/types/collaborator';
import { NewCollaborator } from '@services/collaborator/types/new-collaborator';

// Others
import { getTrackIdFromPath } from '@webapp/utils/tracks/get-track-id-from-url-path';

// Controllers
import ArtistInviteSearchCtrl from '@webapp/controllers/artist/artist-invite-search';
import CollaboratorListCtrl, {
  UpdateCollaboratorsPlatforms,
} from './collaborator-list';

// Components
import Box from '@components/layout/box';
import BoxHeader from '@components/layout/box-header';
import ErrorMessage from '@components/basics/error-message/index';

interface CollaboratorsCtrlProps {
  id?: string;
  className?: string;
  children?: React.ReactNode;
  hint?: React.ReactNode;
  roleList: Option[];
  label: React.ReactNode;
  text?: React.ReactNode;

  assets: CollaboratorState['assets'];

  firstBigger?: boolean;
  inHalf?: boolean;

  requiredRole?: string;
  defaultRole?: string;

  songName: string;

  collaboratorType: ValidType;
  assetId: string;

  locked?: boolean;
  filterRepeated?: boolean;
  forceEdit?: boolean;
  validate?: boolean;

  requirePlatformIds?: boolean;
  isAlbum: boolean;
  showErrors?: boolean;

  hasTabsSelector?: boolean;

  canBeWithoutInvite?: boolean;
  hideEmailInput?: boolean;

  onChange: (data: UpdateCollaboratorsPlatforms[]) => void;
  onRemove: (id: string) => void;

  handleEmailOrPhoneChange: (newContact: ContactInfo) => void;
  showError: React.ReactNode;
  setShowError: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  setNewCollaborator: React.Dispatch<
    React.SetStateAction<NewCollaborator | undefined>
  >;
  setCollaboratorName: React.Dispatch<React.SetStateAction<string>>;
}

interface StateProps {
  missingMainArtist?: boolean;
  missingContributorRole?: boolean;
  missingComposer?: boolean;
  missingLyricist?: boolean;
  isLoading: boolean;
  currentArtistId: string;
  releaseId?: string;
  createCollaborator: (newCollaborator: NewCollaborator) => CreateCollaborator;
  createArtistCollaborator: (
    newCollaborator: NewCollaborator,
    name: string,
    externalArtist?: ArtistSearchResult
  ) => CreateArtistCollaborator;
  updateCollaboratorRole: (
    assetId: string,
    collaboratorId: string,
    value: string
  ) => UpdateCollaboratorField<'role'>;
  removeCollaborator: (newCollaborator: NewCollaborator) => RemoveCollaborator;
  updateCollaboratorOrder: (
    assetId: string,
    newOrder: string[]
  ) => UpdateCollaboratorOrder;
  closeMenu: () => CloseMenu;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
}

const mapStateToProps = (state: AppState) => {
  const activeTrackId = getTrackIdFromPath(state);
  const allAssets = state.collaborator.assets;
  const tracks = Object.keys(state.tracks.tracks) || [];
  const activeTrack = state.tracks.tracks[activeTrackId];

  const currentReleaseAssets = Object.entries(allAssets).reduce(
    (acc, [key, value]) => {
      if (tracks.find((track) => track === key)) {
        acc[key] = value;
      }
      return acc;
    },
    {} as typeof allAssets
  );

  return {
    assets: currentReleaseAssets,
    releaseId: state.release.release?.id,
    currentArtistId: state.artist.active?.id,
    isLoading: !!state.ui.loading[CREATE_COLLABORATOR],
    isAlbum:
      state.release.release && state.release.release?.trackCount
        ? state.release.release?.trackCount > 1
        : false,
    missingMainArtist: !collaboratorsList(
      state.collaborator,
      activeTrackId
    ).find((a) => a.role === 'main'),
    missingContributorRole:
      contributorsList(state.collaborator, activeTrackId).filter(
        (a) => a.role === ''
      ).length > 0,
    missingComposer:
      contributorsList(state.collaborator, activeTrackId).filter((a) =>
        a.role.includes('Composer')
      ).length === 0,
    missingLyricist:
      activeTrack &&
      activeTrack.type === 'song' &&
      contributorsList(state.collaborator, activeTrackId).filter((a) =>
        a.role.includes('Lyricist')
      ).length === 0,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createCollaborator: (newCollaborator: NewCollaborator) =>
    dispatch(new CreateCollaborator(newCollaborator)),
  createArtistCollaborator: (
    newCollaborator: NewCollaborator,
    name: string,
    externalArtist?: ArtistSearchResult
  ) =>
    dispatch(
      new CreateArtistCollaborator(newCollaborator, name, externalArtist)
    ),
  updateCollaboratorRole: (
    assetId: string,
    collaboratorId: string,
    value: string
  ) =>
    dispatch(
      new UpdateCollaboratorField(assetId, collaboratorId, 'role', value)
    ),
  removeCollaborator: (collaborator: Collaborator) =>
    dispatch(new RemoveCollaborator(collaborator.assetId, collaborator.id)),
  updateCollaboratorOrder: (assetId: string, newOrder: string[]) =>
    dispatch(new UpdateCollaboratorOrder(assetId, newOrder)),
  closeMenu: () => dispatch(new CloseMenu()),
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(error, field, value, releaseId)),
});

export const CollaboratorsCtrl = ({
  filterRepeated = true,
  forceEdit,
  isAlbum,
  ...props
}: CollaboratorsCtrlProps & StateProps) => {
  const { assetId, assets } = props;

  const collaborators = assetCollaborators(
    assets,
    assetId,
    props.collaboratorType
  );

  const [newCollaborator, setNewCollaborator] =
    React.useState<NewCollaborator>();

  React.useEffect(() => {
    handleAddMore();
  }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    props.setNewCollaborator(newCollaborator);
  }, [newCollaborator, setNewCollaborator]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (
      newCollaborator &&
      filterRepeated &&
      collaborators.find(
        (o) =>
          o.collaboratorId &&
          (o.collaboratorId === newCollaborator.collaboratorId ||
            o.name === newCollaborator.name)
      )
    ) {
    }
  }, [collaborators, newCollaborator, filterRepeated]);

  React.useEffect(() => {
    if (props.validate && props.missingMainArtist) {
      props.sendAnalytics(
        'Release Missing Main Artist',
        '',
        'RELEASE.RELEASE.MISSING_MAIN_ARTIST',
        props.releaseId
      );
    }
  }, [props.validate, props.missingMainArtist]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddMore = () => {
    if (props.locked) {
      return;
    }
    setNewCollaborator({
      name: '',
      type: props.collaboratorType,
      role: props.defaultRole || '',
      contact: '',
      contactType: 'email',
      collaboratorType: 'ARTIST',
      assetId: props.assetId,
      assetType: 'TRACK',
      releaseId: props.releaseId,
    });
  };

  const isValid = (collaborator: NewCollaborator) => {
    return !!collaborator?.collaboratorId;
  };

  const handleSaveCollaborator = (collaborator: NewCollaborator) => {
    if (!collaborator || !isValid(collaborator)) {
      return;
    }

    props.createCollaborator(collaborator);
  };

  const handleEdit = <F extends keyof NewCollaborator>(
    field: F,
    value: NewCollaborator[F]
  ) => {
    const update: NewCollaborator = {
      ...newCollaborator,
      [field]: value,
      collaboratorId:
        field === 'name' ? undefined : newCollaborator?.collaboratorId,
    } as NewCollaborator;

    setNewCollaborator(update);

    if (update.collaboratorId) {
      handleSaveCollaborator(update);
    }
  };

  const handleArtistSelection = (value: SimpleArtist) => {
    if (collaborators.find((a) => a.id === value.id)) {
      return;
    }
    if (
      newCollaborator?.role === '' &&
      newCollaborator.type !== 'CONTRIBUTOR'
    ) {
      if (props.setShowError) {
        props.setShowError(<Translate id="INVITE.MISSING_ROLE" />);
      }
      return;
    }

    const update: NewCollaborator = {
      ...newCollaborator,
      name: value.name,
      avatar: value.avatar,
      collaboratorId: value.id,
      contact: value.email,
    } as NewCollaborator;

    setNewCollaborator(update);
    if (update.collaboratorId) {
      handleSaveCollaborator(update);
    }
  };

  const handleExternalArtistSelection = (value: ArtistSearchResult) => {
    const update: NewCollaborator = {
      ...newCollaborator,
      name: value.name,
      avatar: value.avatar,
    } as NewCollaborator;

    setNewCollaborator(update);
    if (update.role) {
      props.createArtistCollaborator(update, value.name, value);
    }
  };

  const handleConfirmName = () => {
    if (
      !newCollaborator ||
      !((newCollaborator.name?.trim() || '').length > 0)
    ) {
      return;
    }
    if (props.collaboratorType === 'CONTRIBUTOR') {
      props.createCollaborator({
        ...newCollaborator,
        role: props.defaultRole || '',
      });
      handleAddMore();
    }
    if (props.collaboratorType === 'COLLABORATOR') {
      props.createArtistCollaborator(
        newCollaborator,
        newCollaborator.name || ''
      );
      handleAddMore();
    }
  };

  const updateOrder = (oldIndex: number, newIndex: number) => {
    const newCollaborators = arrayMove(collaborators, oldIndex, newIndex);

    props.updateCollaboratorOrder(
      props.assetId,
      newCollaborators.map((c) => c.id)
    );
  };

  return (
    <>
      <Box className={props.className} noBorderTop noBorderBottom>
        <BoxHeader title={props.label} text={props.text} />
        {props.children ? props.children : null}
        {!!newCollaborator ? (
          <ArtistInviteSearchCtrl
            id={props.id}
            className="artist-invite-input mb-20"
            label={
              props.collaboratorType === 'COLLABORATOR' ? (
                <Translate id="RELEASE.TRACK_INFO.TRACK.COLLABORATOR.INPUT.NEW.LABEL" />
              ) : (
                <Translate id="RELEASE.TRACK_INFO.TRACK.CREDIT.INPUT.NEW.LABEL" />
              )
            }
            value={newCollaborator}
            filterRepeated={filterRepeated}
            currentValues={collaborators}
            role={newCollaborator.role}
            locked={props.isLoading || props.locked}
            type={
              props.collaboratorType === 'COLLABORATOR'
                ? 'collaborator'
                : 'credits'
            }
            source={{
              type: props.collaboratorType,
              assetType: 'TRACK',
              id: props.assetId,
            }}
            firstBigger={props.firstBigger}
            inHalf={props.inHalf}
            songName={props.songName}
            onChange={(value) => handleEdit('name', value)}
            onSelection={(value) => {
              if (props.collaboratorType === 'COLLABORATOR') {
                handleExternalArtistSelection(value as ArtistSearchResult);
              }
              if (props.collaboratorType === 'CONTRIBUTOR') {
                handleArtistSelection(value as SimpleArtist);
              }
              handleAddMore();
            }}
            onInvite={handleAddMore}
            onConfirmName={handleConfirmName}
            releaseId={props.releaseId}
            customBorderRadius="4px 0 0 4px"
            dataType="collaborator"
            handleEmailOrPhoneChange={(value) => {
              handleEdit('contact', value.value);
              props.handleEmailOrPhoneChange(value);
            }}
            showError={props.showError}
            setShowError={props.setShowError}
            setName={props.setCollaboratorName}
            hasTabsSelector={props.hasTabsSelector}
            canBeWithoutInvite={props.canBeWithoutInvite}
            hideEmailInput={props.hideEmailInput}
          />
        ) : null}

        <CollaboratorListCtrl
          onChange={(data) => {
            props.onChange(data);
          }}
          collaborators={collaborators}
          firstBigger={props.firstBigger}
          inHalf={props.inHalf}
          disabledIds={forceEdit || isAlbum ? [] : [props.currentArtistId]}
          requiredRole={props.requiredRole}
          roleList={props.roleList}
          currentArtistId={props.currentArtistId}
          removeCollaborator={(collaborator) => {
            if (props.locked) {
              return null;
            }
            props.removeCollaborator(collaborator);
            props.onRemove(collaborator.id);
          }}
          updateCollaboratorRole={
            props.locked ? () => null : props.updateCollaboratorRole
          }
          updateCollaboratorsOrder={props.locked ? () => null : updateOrder}
          requirePlatformIds={props.requirePlatformIds}
          showErrors={props.showErrors}
          hasMultiSelect={props.collaboratorType === 'CONTRIBUTOR'}
        />

        {props.hint}

        {!props.validate && (
          <ErrorMessage
            className="mt-10"
            hasBackground
            errorId={
              props.missingMainArtist
                ? 'RELEASE.MISSING_MAIN_ARTIST'
                : undefined
            }
          />
        )}
        {props.collaboratorType === 'CONTRIBUTOR' &&
          props.missingContributorRole && (
            <ErrorMessage
              className="mt-10"
              hasBackground
              errorId={
                props.missingContributorRole
                  ? 'RELEASE.MISSING_COLLABORATOR_ROLE'
                  : undefined
              }
            />
          )}
        {props.collaboratorType === 'CONTRIBUTOR' && props.missingComposer && (
          <ErrorMessage
            className="mt-10"
            hasBackground
            errorId={
              props.missingComposer ? 'RELEASE.MISSING_COMPOSER' : undefined
            }
          />
        )}
        {props.collaboratorType === 'CONTRIBUTOR' && props.missingLyricist && (
          <ErrorMessage
            className="mt-10"
            hasBackground
            errorId={
              props.missingLyricist ? 'RELEASE.MISSING_LYRICIST' : undefined
            }
          />
        )}
      </Box>
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(CollaboratorsCtrl)`
  .decoration-right {
    z-index: 1;
  }

  .menu {
    .open-icon,
    .close-icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
    }
  }

  .artist-invite-input {
    flex-grow: 1;
    flex-shrink: 1;
  }
  .artist-role-selector {
    flex-shrink: 0;
  }
`);
